gsap.registerPlugin(ScrollTrigger);
export function swiperInit() {

	function getSlidesPerGroup(slidesSizesGrid, visibleSlidesIndexes) {
		const totalSlide = slidesSizesGrid.length;
		const visibleSlide = visibleSlidesIndexes?.length ?? 1;

		let slidesPerGroup = visibleSlide;

		if (totalSlide <= visibleSlide) {
			slidesPerGroup = null;
		}
		return slidesPerGroup;
	}
	/**
	 * Force-load lozad images for the active slide and peeks ahead.
	 * Fractional --spv (1.2 / 1.3) often fails IntersectionObserver (threshold 0.2),
	 * so we must call triggerLoad (with a data-src→src fallback).
	 */
	function triggerLozadNextSlideImage(swiper) {
		if (!swiper?.el) return;

		const loader = window.lozadObserver || window.lazyLoader;

		const loadEl = (el) => {
			if (!el || el.getAttribute("data-loaded") === "true") return;

			if (loader && typeof loader.triggerLoad === "function") {
				loader.triggerLoad(el);
			}

			// Fallback if triggerLoad no-ops or loader missing
			if (
				el.getAttribute("data-loaded") !== "true" &&
				el.getAttribute("data-src") &&
				!el.getAttribute("src")
			) {
				el.src = el.getAttribute("data-src");
				el.setAttribute("data-loaded", "true");
			}
		};

		const loadInSlide = (slide) => {
			if (!slide) return;
			slide.querySelectorAll("img.lozad, .lozad, img[data-src]").forEach(loadEl);
		};

		// 1) Visible / peek slides (most reliable with slidesPerView: auto + --spv)
		swiper.el
			.querySelectorAll(
				".swiper-slide-visible, .swiper-slide-active, .swiper-slide-next, .swiper-slide-prev"
			)
			.forEach(loadInSlide);

		// 2) Index look-ahead (covers slides not yet classed as visible)
		const lookAheadCount = 4;
		const start = swiper.activeIndex ?? 0;
		for (let i = 0; i <= lookAheadCount; i++) {
			loadInSlide(swiper.slides[start + i]);
		}

		// 3) visibleSlidesIndexes when Swiper exposes them
		const indexes = swiper.visibleSlidesIndexes;
		if (Array.isArray(indexes)) {
			indexes.forEach((idx) => loadInSlide(swiper.slides[idx]));
			const last = indexes[indexes.length - 1];
			if (Number.isFinite(last)) {
				for (let i = 1; i <= 2; i++) {
					loadInSlide(swiper.slides[last + i]);
				}
			}
		}
	}
	function autoSetSlidesPerGroup(swiper) {
		if (!swiper.pagination.el) return;
		const { visibleSlidesIndexes, slidesSizesGrid } = swiper;
		const slidesPerGroup = getSlidesPerGroup(
			slidesSizesGrid,
			visibleSlidesIndexes
		);
		if (
			slidesPerGroup != null &&
			slidesPerGroup > 0 &&
			slidesPerGroup !== swiper.params.slidesPerGroup
		) {
			swiper.params.slidesPerGroup = slidesPerGroup;
			swiper.update();
		}
	}

	/**
	 * Resolve marquee travel speed (ms per avg slide).
	 * data-speed-mobile: slower on small viewports when set (higher ms = slower).
	 */
	function resolveMarqueeSpeed(el, desktopSpeed) {
		const mobileRaw = el.getAttribute("data-speed-mobile");
		const mobileSpeed = mobileRaw != null ? Number(mobileRaw) : NaN;
		const isMobile = window.matchMedia("(max-width: 767px)").matches;
		if (isMobile && Number.isFinite(mobileSpeed) && mobileSpeed > 0) {
			return mobileSpeed;
		}
		return desktopSpeed > 0 ? desktopSpeed : 4000;
	}

	/**
	 * Run `startFn` once the element first enters the viewport, then stop observing.
	 * Used by data-autoplay-inview — after the first scroll-into-view we never pause/resume.
	 */
	function runOnceWhenInView(el, startFn) {
		if (!el || typeof startFn !== "function") return;

		if (!("IntersectionObserver" in window)) {
			startFn();
			return;
		}

		const observer = new IntersectionObserver(
			(entries) => {
				if (!entries.some((entry) => entry.isIntersecting)) return;
				observer.disconnect();
				startFn();
			},
			{ root: null, threshold: 0.15 }
		);

		observer.observe(el);
	}

	/**
	 * Resolve .init-swiper --mr to a pixel number (plain px, clamp, calc, media queries).
	 * parseFloat(getPropertyValue('--mr')) fails on clamp()/calc() → 0 gap on xl.
	 */
	function resolveMrGap(el) {
		if (!el) return 0;
		const raw = getComputedStyle(el).getPropertyValue("--mr").trim();
		if (
			raw &&
			!raw.startsWith("clamp") &&
			!raw.startsWith("calc") &&
			!raw.startsWith("var") &&
			!raw.startsWith("max") &&
			!raw.startsWith("min")
		) {
			const simple = parseFloat(raw);
			if (Number.isFinite(simple)) return simple;
		}

		const probe = document.createElement("div");
		probe.setAttribute("aria-hidden", "true");
		probe.style.cssText =
			"position:absolute;visibility:hidden;pointer-events:none;margin:0;padding:0;border:0;width:0;height:0;margin-right:var(--mr)";
		el.appendChild(probe);
		const px = parseFloat(getComputedStyle(probe).marginRight) || 0;
		probe.remove();
		return px;
	}

	/**
	 * Smooth continuous marquee scroll using requestAnimationFrame.
	 * Uses duplicated slides + translate wrap (Swiper 9+ loop has no DOM clones
	 * and loopFix() fights continuous setTranslate).
	 *
	 * Owns translate via a local `offset` — never read getTranslate() each frame.
	 * Swiper observer/update can reset the wrapper to 0 when the section re-enters
	 * view on scroll; a local offset + re-apply avoids that jump.
	 *
	 * @param {Swiper} swiper - The initialized Swiper instance
	 * @param {() => number} getSpeed - returns current ms-per-slide (desktop/mobile)
	 * @param {Element} [parentEl] - .init-swiper (reads responsive --mr on resize)
	 */
	function startMarqueeScroll(swiper, getSpeed, parentEl) {
		const isReverse = parentEl ? parentEl.classList.contains("reverse-direction") : false;
		const wrapperEl = swiper.wrapperEl;
		const gap = () => Number(swiper.params.spaceBetween) || 0;

		const originalCount = () =>
			wrapperEl.querySelectorAll(
				".swiper-slide:not([data-marquee-clone])"
			).length;

		const getStripWidth = () => {
			const n = originalCount();
			if (!n) return 0;
			// Offset of first clone == width of one original strip (incl. gaps)
			if (swiper.slidesGrid?.[n] != null) return swiper.slidesGrid[n];
			const sizes = swiper.slidesSizesGrid?.slice(0, n) || [];
			return sizes.reduce((a, b) => a + b, 0) + gap() * n;
		};

		const getPxPerMs = () => {
			const sizes = swiper.slidesSizesGrid;
			const speed = typeof getSpeed === "function" ? getSpeed() : getSpeed;
			if (!sizes?.length || !(speed > 0)) return 0;
			const avg = sizes.reduce((a, b) => a + b, 0) / sizes.length;
			return (avg + gap()) / speed;
		};

		const syncGapFromCss = () => {
			if (!parentEl) return;
			const next = resolveMrGap(parentEl);
			if (next === swiper.params.spaceBetween) return;
			swiper.params.spaceBetween = next;
			swiper.update();
		};

		let lastTime = null;
		let pxPerMs = getPxPerMs();
		let stripWidth = getStripWidth();
		// Local source of truth — do not derive from getTranslate() (observer can zero it)
		let offset = isReverse && stripWidth > 0 ? -stripWidth : 0;

		const applyOffset = () => {
			swiper.setTranslate(offset);
			wrapperEl.style.transitionDuration = "0ms";
		};

		wrapperEl.style.transitionDuration = "0ms";

		swiper.on("resize", () => {
			syncGapFromCss();
			pxPerMs = getPxPerMs();
			stripWidth = getStripWidth();
			if (stripWidth > 0) {
				offset = ((offset % stripWidth) + stripWidth) % stripWidth;
				// Keep negative direction (scroll left)
				if (offset > 0) offset -= stripWidth;
			}
			applyOffset();
		});

		// Recalc when crossing mobile/desktop breakpoint (speed / --mr may change)
		const mq = window.matchMedia("(max-width: 767px)");
		const onMq = () => {
			syncGapFromCss();
			pxPerMs = getPxPerMs();
			stripWidth = getStripWidth();
		};
		if (typeof mq.addEventListener === "function") {
			mq.addEventListener("change", onMq);
		} else if (typeof mq.addListener === "function") {
			mq.addListener(onMq);
		}

		(function tick(now) {
			if (lastTime !== null && pxPerMs > 0) {
				const dt = Math.min(now - lastTime, 64);

				if (isReverse) {
					offset += pxPerMs * dt;
					if (stripWidth > 0 && offset >= 0) {
						offset -= stripWidth;
					}
				} else {
					offset -= pxPerMs * dt;
					if (stripWidth > 0 && offset <= -stripWidth) {
						offset += stripWidth;
					}
				}

				applyOffset();
			}
			lastTime = now;
			requestAnimationFrame(tick);
		})(performance.now());
	}

	/**
	 * Clone original slides at least once (seamless wrap), then until
	 * content is ≥ 2× the viewport wide.
	 */
	function fillMarqueeClones(swiper) {
		const wrapper = swiper.wrapperEl;
		const originals = [
			...wrapper.querySelectorAll(
				".swiper-slide:not([data-marquee-clone])"
			),
		];
		if (!originals.length) return;

		const gap = Number(swiper.params.spaceBetween) || 0;
		const measure = () =>
			[...wrapper.children].reduce(
				(sum, el) => sum + el.offsetWidth + gap,
				0
			);
		const appendCopies = () => {
			originals.forEach((slide) => {
				const clone = slide.cloneNode(true);
				clone.setAttribute("data-marquee-clone", "true");
				clone.setAttribute("aria-hidden", "true");
				wrapper.appendChild(clone);
			});
		};

		// Always duplicate once so translate-wrap has matching content ahead
		appendCopies();

		let guard = 0;
		while (measure() < swiper.size * 2 + 1 && guard < 5) {
			appendCopies();
			guard += 1;
		}

		swiper.update();
	}






	window.triggerFadeUp = function (slide) {
		slide?.querySelectorAll(".text-fd-up, .btn-wrap").forEach((el) => {
			el.classList.remove("is-visible");
			void el.offsetWidth; // force reflow
			el.classList.add("is-visible");
		});
	};

	// 1. Initialize all Video.js players
	document.querySelectorAll('.video-js').forEach((videoEl, index) => {
		if (!videoEl.id) videoEl.id = `vjs-player-${index}`;

		const { type, src } = videoEl.dataset;

		// Save the player instance to a variable
		const player = videojs(videoEl.id, {
			controls: true,
			autoplay: false,
			preload: 'auto',
			muted: true,
			playsinline: true,
			techOrder: type === 'youtube' ? ['youtube'] : ['html5'],
			...(type === 'youtube' && {
				sources: [{ type: 'video/youtube', src }],
				youtube: {
					customVars: {
						origin: window.location.origin
					}
				}
			})
		});

		// Ensure muted attribute is set on the underlying video element (browser autoplay policy)
		player.ready(() => {
			const tech = player.tech({ IWillNotUseThisInPlugins: true });
			if (tech && tech.el_) tech.el_.muted = true;
		});

		// --- MOBILE TOUCH FIX ---
		player.on('touchstart', function (e) {
			// Check if the user is tapping the video canvas directly
			if (e.target.nodeName === 'VIDEO') {
				if (player.paused()) {
					player.play();
				} else {
					player.pause();
				}
			}
		});
	});
	// 2. Initialize Swiper
	const primarySwiper = new Swiper(".primary-banner .swiper", {
		slidesPerView: 1,
		speed: 1205,
		allowTouchMove: true,
		effect: 'fade',
		fadeEffect: { crossFade: true },
		init: false,
		noSwipingClass: 'video-js',
		rewind: true,
		navigation: {
			nextEl: ".primary-banner .next",
			prevEl: ".primary-banner .prev",
		},
		pagination: {
			el: ".primary-banner .swiper-pagination",
			clickable: true,
		},
	});

	const progressTrack = document.querySelector(".primary-banner .progress-track");
	let progressRaf = 0;
	let progressOnEnd = null;
	let progressPlayer = null;
	let isPaused = false;
	const SLIDE_TIMEOUT = 5000;

	function setProgressScale(p) {
		if (!progressTrack) return;
		progressTrack.style.transform = `scaleX(${p < 0 ? 0 : p > 1 ? 1 : p})`;
	}

	function stopProgressTrack() {
		if (progressRaf) {
			cancelAnimationFrame(progressRaf);
			progressRaf = 0;
		}
		if (progressTrack) {
			if (progressOnEnd) {
				progressTrack.removeEventListener("animationend", progressOnEnd);
				progressOnEnd = null;
			}
			progressTrack.style.animation = "none";
			progressTrack.style.animationPlayState = "";
		}
		progressPlayer = null;
		setProgressScale(0);
	}

	function tickVideoProgress() {
		progressRaf = 0;
		if (!progressPlayer || isPaused) return;
		const duration = progressPlayer.duration();
		if (duration > 0 && Number.isFinite(duration)) {
			setProgressScale(progressPlayer.currentTime() / duration);
		}
		progressRaf = requestAnimationFrame(tickVideoProgress);
	}

	function syncProgressPause() {
		if (progressTrack?.style.animation && progressTrack.style.animation !== "none") {
			progressTrack.style.animationPlayState = isPaused ? "paused" : "running";
		}
		if (!progressPlayer) return;
		if (isPaused) {
			if (progressRaf) {
				cancelAnimationFrame(progressRaf);
				progressRaf = 0;
			}
			return;
		}
		if (!progressRaf) progressRaf = requestAnimationFrame(tickVideoProgress);
	}

	// 2.5 Pause Track Toggle
	const pauseTrackBtn = document.querySelector(".primary-banner .pause-track");
	if (pauseTrackBtn) {
		pauseTrackBtn.addEventListener("click", () => {
			isPaused = !isPaused;
			pauseTrackBtn.classList.toggle("is-paused", isPaused);
			syncProgressPause();

			const currentSlide = primarySwiper.slides[primarySwiper.activeIndex];
			if (!currentSlide) return;

			const videoElement = currentSlide.querySelector('.video-js');
			if (videoElement) {
				const player = videojs(videoElement.id);
				if (isPaused) {
					player.pause();
				} else {
					player.play();
				}
			}
		});
	}

	const goNextSlide = () => {
		primarySwiper.slideNext();
	};

	function resetProgressTrack() {
		stopProgressTrack();
	}

	function startImageProgressTrack() {
		stopProgressTrack();
		if (!progressTrack) return;

		progressOnEnd = (e) => {
			if (e.target !== progressTrack) return;
			progressOnEnd = null;
			goNextSlide();
		};
		progressTrack.addEventListener("animationend", progressOnEnd);

		progressTrack.style.transform = "scaleX(0)";
		progressTrack.style.animation = "none";
		void progressTrack.offsetWidth;
		progressTrack.style.animation = `banner-progress-fill ${SLIDE_TIMEOUT}ms linear forwards`;
		progressTrack.style.animationPlayState = isPaused ? "paused" : "running";
	}

	function startVideoProgressTrack(player) {
		stopProgressTrack();
		if (!progressTrack) return;
		progressPlayer = player;
		if (!isPaused) progressRaf = requestAnimationFrame(tickVideoProgress);
	}

	// 3. Playback Handler
	function handleVideoPlayback(slide) {
		resetProgressTrack();
		if (!slide) return;

		const videoElement = slide.querySelector('.video-js');

		if (videoElement) {
			const player = videojs(videoElement.id);

			player.ready(() => {
				player.off('ended');
				player.on('ended', () => { goNextSlide(); });
				player.currentTime(0);

				startVideoProgressTrack(player);

				if (!isPaused) {
					const tryPlay = () => {
						// Ensure muted before play to satisfy browser autoplay policy
						player.muted(true);

						const playPromise = player.play();
						if (playPromise !== undefined) {
							playPromise.then(() => {
								// Playback successful
							}).catch(err => {
								startImageTimer();
							});
						}
					};

					// For mp4 (html5), wait for canplay before attempting play
					const isHtml5 = player.techName_ === 'Html5' || !player.options_.techOrder?.includes('youtube');
					if (isHtml5 && player.readyState() < 3) {
						player.one('canplay', tryPlay);
					} else {
						tryPlay();
					}
				}
			});
		} else {
			startImageTimer();
		}
	}

	function startImageTimer() {
		startImageProgressTrack();
	}

	// 4. Swiper Events
	primarySwiper.on("slideChangeTransitionStart", () => {
		resetProgressTrack();
		document.querySelectorAll('.video-js').forEach((el) => {
			const player = videojs(el.id);
			if (player && !player.paused()) player.pause();
		});
		document.querySelectorAll(".primary-banner .btn-wrap.is-visible, .primary-banner .text-fd-up.is-visible").forEach((el) => {
			el.classList.remove("is-visible");
		});
	});

	primarySwiper.on("slideChangeTransitionEnd", () => {
		const currentSlide = primarySwiper.slides[primarySwiper.activeIndex];
		handleVideoPlayback(currentSlide);
		triggerFadeUp(currentSlide);
	});

	// 5. Init
	primarySwiper.init();

	const initialSlide = primarySwiper.slides[primarySwiper.activeIndex];
	handleVideoPlayback(initialSlide);
	triggerFadeUp(initialSlide);





	window.initSwiper = [];

	$(".init-swiper .swiper").each(function (index) {
		const $this = $(this);

		// Flags
		const isLoop = $this.hasClass("is-loop");
		const isCenter = $this.hasClass("is-center-slide");
		// data-autoplay-inview: defer per-slide autoplay until first viewport entry (once).
		// Ignored for marquee (time === 0) — deferred start causes a translate jump on scroll-back.
		const autoplayInView = $this[0].hasAttribute("data-autoplay-inview");

		// Data attrs
		let time = Number($this.data("time"));
		let dataspeed = Number($this.data("speed"));
		let initialSlide = Number($this.data("initial"));

		// Defaults
		if (!Number.isFinite(time)) time = 5000;
		if (!Number.isFinite(dataspeed)) dataspeed = 1205;


		// Pagination type
		let paginationType = "bullets";

		if ($this.hasClass("fraction")) {
			paginationType = "fraction";
		} else if ($this.hasClass("progress")) {
			paginationType = "progressbar";
		}

		// Unique classes
		$this.addClass("swiper-init-" + index);

		const $parent = $this.parents(".init-swiper");

		// Only this page (.duan-detail-1):
		// When loop mode has 3 or fewer slides with centeredSlides/peeking,
		// Swiper lacks enough DOM elements on both sides during transitions.
		// Count slides and if <= 3, duplicate them so Swiper registers them as swiper-slide.
		const isExampleCenter = $this.closest(".swiper-example-center").length > 0;
		if (isExampleCenter && isLoop) {
			const $wrapper = $this.children(".swiper-wrapper");
			const $slides = $wrapper.children(".swiper-slide");
			if ($slides.length > 0 && $slides.length <= 3) {
				const repeat = Math.ceil(6 / $slides.length);
				for (let r = 1; r < repeat; r++) {
					$slides.each(function () {
						const $clone = $(this).clone(true);
						$wrapper.append($clone);
					});
				}
				window.lozadObserver?.observe?.();
			}
		}

		$parent.find(".prev").addClass("prev-nav-" + index);
		$parent.find(".next").addClass("next-nav-" + index);
		$parent.find(".swiper-pagination").addClass("pagination-inst-" + index);

		// Base options
		const options = {
			observer: true,
			observeParents: true,

			rewind: !isLoop,
			loop: isLoop,

			speed: dataspeed,

			watchSlidesProgress: true,
			slidesPerView: "auto",

			centeredSlides: isCenter,

			// Images: lozad via window.lozadObserver + triggerLozadNextSlideImage (not Swiper lazy)

			autoplay: {
				delay: time,
				pauseOnMouseEnter: true,
				disableOnInteraction: false,
				// Stay armed but idle until first viewport entry (data-autoplay-inview)
				enabled: !autoplayInView,
			},

			navigation: {
				nextEl: ".next-nav-" + index,
				prevEl: ".prev-nav-" + index,
			},

			pagination: {
				el: ".pagination-inst-" + index,
				type: paginationType,
				clickable: true,
				dynamicMainBullets: 1,
			},

			on: {
				beforeInit: function () {
					window.initSwiper.push(this);
				},

				init: function () {
					// Fix duplicate fancybox in loop mode
					if (isLoop) {
						// Re-scan so Swiper's cloned slides get registered with the observer
						window.lozadObserver?.observe();

						$(this.el)
							.find(".swiper-slide-duplicate a[data-fancybox]")
							.removeAttr("data-fancybox");
					}

					const sw = this;
					// Defer until Swiper has applied visible/next classes (fractional --spv)
					requestAnimationFrame(() => {
						triggerLozadNextSlideImage(sw);
						requestAnimationFrame(() => triggerLozadNextSlideImage(sw));
					});


				},

				slideChange: function () {
					// Pause videos when slide changes
					if ($this.hasClass("slide-has-video")) {
						const $videos = $this.find("video");

						if ($videos.length) {
							$videos[0].pause();
						}

						$this.find(".swiper-slide").removeClass("play-video");
						$this.find(".play-btn").removeClass("active");
					}

					triggerLozadNextSlideImage(this);
				},

				slideChangeTransitionStart: function () {
					triggerLozadNextSlideImage(this);
				},

				slideChangeTransitionEnd: function () {
					triggerLozadNextSlideImage(this);
				},

				resize: function (swiper) {
					if (isCenter) {
						const next = isExampleCenter ? 24 : resolveMrGap($parent[0]);
						if (next !== Number(swiper.params.spaceBetween)) {
							swiper.params.spaceBetween = next;
							swiper.update();
							return;
						}
					}

					const totalSlidesWidth = swiper.slidesSizesGrid.reduce(
						(acc, curr) => acc + curr,
						0
					);

					swiper.el.classList.toggle(
						"center-slide-active",
						totalSlidesWidth < swiper.size
					);

					// Marquee (time === 0): skip — slidesPerGroup bump breaks loop bookkeeping
					if (time !== 0) {
						autoSetSlidesPerGroup(swiper);
					}
				},
			},
		};

		// Better center mode behavior
		if (isCenter) {
			// centeredSlidesBounds snaps first/last to the edge — breaks loop
			// peeking (active sits off-center, two slides look left-aligned)
			options.centeredSlidesBounds = !isLoop;

			// CSS margin-right is included in auto slideSize and shifts the card
			// left by --mr/2. Use spaceBetween (stylesheet margin zeroed in sass).
			options.spaceBetween = isExampleCenter ? 24 : resolveMrGap($parent[0]);

			// If swiper doesn't explicitly hide overflow, it wants peeking slides
			// Disable clip-path so prev/next slides aren't clipped to blank space
			if (!$this.hasClass("overflow-hidden")) {
				$parent.css("--swiper-clip", "none");
			}
		} else if (isExampleCenter) {
			options.spaceBetween = 24;
		}

		// Initial slide
		if (Number.isFinite(initialSlide)) {
			options.initialSlide = initialSlide;
		}

		// Continuous autoplay mode (marquee)
		if (time === 0) {
			// Swiper 9+ loop rearranges slides (no DOM clones) and loopFix()
			// fights continuous setTranslate — use clones + manual wrap instead.
			options.loop = false;
			options.rewind = false;
			options.slidesPerGroup = 1;
			options.autoplay = false;
			options.speed = 0;
			options.allowTouchMove = false;
			options.grabCursor = false;
			options.watchOverflow = false;
			// observer/observeParents fire observerUpdate on scroll-into-view and
			// reset wrapper translate to 0 — that is the marquee "shift" jump.
			options.observer = false;
			options.observeParents = false;
			options.observeSlideChildren = false;

			delete options.navigation;
			delete options.pagination;

			const mrValue = resolveMrGap($parent[0]);
			options.spaceBetween = mrValue;

			$parent.addClass("is-marquee");
		}


		// Init
		const swiperInstance = new Swiper(".swiper-init-" + index, options);

		// Start custom marquee animation (replaces Swiper's per-slide autoplay)
		// Marquee always starts immediately — deferring via data-autoplay-inview
		// causes a visible translate jump when scrolling back into view.
		if (time === 0) {
			fillMarqueeClones(swiperInstance);
			// Clones are new DOM nodes — re-scan, then force-load (overflow/transform skips IO)
			window.lozadObserver?.observe?.();
			swiperInstance.el
				.querySelectorAll("img.lozad, .lozad, img[data-src]")
				.forEach((el) => {
					window.lozadObserver?.triggerLoad?.(el);
					if (
						el.getAttribute("data-loaded") !== "true" &&
						el.getAttribute("data-src") &&
						!el.getAttribute("src")
					) {
						el.src = el.getAttribute("data-src");
						el.setAttribute("data-loaded", "true");
					}
				});
			const desktopSpeed = dataspeed || 4000;
			startMarqueeScroll(
				swiperInstance,
				() => resolveMarqueeSpeed($this[0], desktopSpeed),
				$parent[0]
			);
		} else if (autoplayInView) {
			// Per-slide autoplay: start once on first viewport entry, never stop after
			runOnceWhenInView($this[0], () => {
				swiperInstance.autoplay?.start?.();
			});
		}
	});

	var swiperSync1Thumb = new Swiper(".swiper-sync-1 .swiper-thumb .swiper", {
		spaceBetween: 20,
		breakpoints: {
			200: {
				spaceBetween: 10,
				slidesPerView: 3,
			},
			576: {
				slidesPerView: 4,
			},
			1024: {
				slidesPerView: 5,
			},
			1200: {
				slidesPerView: 7,
			},

		},
		speed: 300,
		noSwiping: true,
		lazy: {
			loadPrevNext: true,
		},
	});

	var swiperSync1Main = new Swiper(".swiper-sync-1 .swiper-main .swiper", {
		spaceBetween: 40,
		centeredSlides: true,
		speed: 1205,
		slidesPerView: 1,
		autoplay: {
			delay: 5000,
		},
		slideToClickedSlide: true,
		lazy: {
			loadPrevNext: true,
		},
		navigation: {
			nextEl: ".swiper-sync-1 .next",
			prevEl: ".swiper-sync-1 .prev",
		},
		thumbs: {
			swiper: swiperSync1Thumb,
		},
	});
	var gridSwiper = new Swiper(".partner-grid-swiper .swiper", {
		preventInteractionOnTransition: true,
		observer: true,
		observeParents: true,
		rewind: true,
		speed: 1205,
		autoplay: {
			delay: 4000,
			pauseOnMouseEnter: true,
		},
		lazy: {
			loadPrevNext: true,
		},
		spaceBetween: 15,
		simulateTouch: false,
		breakpoints: {

			300: {
				slidesPerView: 2,
				spaceBetween: 8,
				grid: {
					rows: 2,
					fill: "row",
				},
			},

			768: {
				slidesPerView: 3,
				grid: {
					rows: 2,
					fill: "row",
				},
			},
			1024: {
				slidesPerView: 4,
				spaceBetween: 16,
				grid: {
					rows: 2,
					fill: "row",
				},
			},

		},
		navigation: {
			nextEl: ".partner-grid-swiper .next",
			prevEl: ".partner-grid-swiper .prev",
		},
	});
}